<template>
  <div ref="galleryLightbox" class="gallery-lightbox">
    <div class="gallery-lightbox__body">
      <div class="gallery-lightbox__header">
        <div class="gallery-lightbox__object-name">
          <span class="object-name">
            {{ objectName }}
          </span>
          <T1Stars class="object-category" v-if="category" :number="category" />
        </div>
        <div class="gallery-lightbox__close">
          <div @click="closeLightbox" class="close-button">
            <span></span>
            <span></span>
          </div>
        </div>
      </div>

      <div class="gallery-lightbox__main-stage">
        <div class="main-slider">
          <splide
            ref="mainSlider"
            v-if="slides.length"
            class="main-slider__stage"
            :options="mainSliderOptions"
            @splide:moved="onMove"
          >
            <splide-slide v-for="(slide, idx) in slides" :key="idx">
              <div class="main-slider__image">
                <img
                  :data-splide-lazy="slide.txt"
                  @error="imageLoadError"
                  alt=""
                />
              </div>
            </splide-slide>
          </splide>
        </div>
      </div>
      <div class="gallery-lightbox__counter">
        <span>{{ currentIndex }} / {{ slides.length }}</span>
      </div>
      <div class="gallery-lightbox__thumbs">
        <div class="thumbs-slider">
          <splide :options="thumbsSliderOptions" ref="thumbsSlider">
            <splide-slide v-for="(slide, idx) in slides" :key="idx">
              <div class="thumbs-slider__image">
                <img
                  :data-splide-lazy="slide.txt"
                  @error="imageLoadError"
                  alt=""
                />
              </div>
            </splide-slide>
          </splide>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import T1Stars from "../T1Shared/T1Stars";

export default {
  components: {
    T1Stars,
  },
  props: {
    isOpen: Boolean,
    slides: Array,
    objectName: String,
    category: String,
    startSlideIndex: [String, Number],
  },
  data: () => ({
    mainSliderOptions: {
      type: "fade",
      lazyLoad: "nearby",
      preloadPages: 2,
      pagination: false,
      rewind: true,
    },
    thumbsSliderOptions: {
      arrows: false,
      lazyLoad: "sequential",
      type: "slide",
      rewind: true,
      pagination: false,
      fixedWidth: 144,
      fixedHeight: 82,
      focus: "left",
      isNavigation: true,
      speed: 250,
    },
    defaultImage: "https://www.travelone.pl/newimgs/zaslepka_lato.jpg",
    currentIndex: 1,
  }),
  methods: {
    closeLightbox() {
      const lightbox = this.$refs.galleryLightbox;
      lightbox.classList.remove("_visible");

      setTimeout(() => {
        this.$emit("clicked", false);
      }, 150);
    },
    onLightboxShow() {
      const lightbox = this.$refs.galleryLightbox;
      lightbox.classList.add("_visible");
    },
    toggleBodyClass(active) {
      const bodyElem = document.body;
      let supportsTouch =
        "ontouchstart" in window || navigator.msMaxTouchPoints;

      if (active) {
        bodyElem.style.overflow = "hidden";
        if (!supportsTouch) {
          bodyElem.style.paddingRight = "17px";
        }
      } else {
        bodyElem.style.overflow = "";
        bodyElem.style.paddingRight = "";
      }
    },
    imageLoadError(e) {
      e.target.src = this.defaultImage;
    },
    onMove() {
      this.currentIndex = this.$refs.mainSlider.splide.index + 1;
    },
  },
  mounted() {
    this.toggleBodyClass(true);
    this.$refs.mainSlider.sync(this.$refs.thumbsSlider.splide);
    setTimeout(() => {
      this.onLightboxShow();
      this.$refs.mainSlider.splide.go(this.startSlideIndex);
    }, 0);
  },
  destroyed() {
    this.toggleBodyClass(false);
  },
};
</script>

<style lang="scss" scoped>
.gallery-lightbox {
  background-color: rgba(#000, 0.915);
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 99999;
  opacity: 0;
  transition: all 0.15s ease-in-out;

  &._visible {
    opacity: 1;
  }

  &__body {
    display: flex;
    flex-direction: column;
    overflow: hidden;
    width: 100%;
    height: 100%;
  }

  &__header {
    display: flex;
    flex: 0 0 50px;
  }

  &__object-name {
    display: flex;
    flex-wrap: wrap;
    padding-left: 16px;
    padding-top: 12px;

    .object-name {
      @include font-l();
      color: #fff;
      font-size: 20px;
      padding-right: 8px;
    }

    .object-category {
      color: darken($yellow-default, 5%);
      font-size: 14px;
      position: relative;
      top: 2px;
    }
  }

  &__close {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 50px;
    width: 50px;
    margin-left: auto;
  }

  &__main-stage {
    flex: 1 1 100%;
    overflow: hidden;
    padding: 16px 0 0;
  }

  &__counter {
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 24px 0;
  }

  &__thumbs {
    flex: 0 0 100px;
  }
}

::v-deep .main-slider {
  .splide__slide,
  .splide__track,
  .splide__list {
    height: 100%;
  }

  .splide__arrow {
    background-color: transparent;
    width: 3em;
    height: 3em;

    svg {
      fill: #fff;
    }

    &:disabled {
      display: none;
    }
  }
}

::v-deep .thumbs-slider {
  .splide__slide {
    border: 0 !important;
    padding: 0 12px;

    &.is-active .thumbs-slider__image::before {
      opacity: 0;
    }
  }
}

.main-slider {
  height: 100%;

  &__stage {
    height: 100%;
  }

  &__image {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    max-width: 1200px;
    margin: 0 auto;

    > img {
      flex: 0 0 auto;
      width: auto;
      max-width: 100%;
      height: auto;
      max-height: 100%;
    }
  }
}

.thumbs-slider {
  width: 100%;

  &__image {
    border-radius: 2px;
    display: flex;
    align-items: center;
    height: 100%;
    width: 100%;
    overflow: hidden;
    position: relative;

    &:hover {
      cursor: pointer;

      &::before {
        opacity: 0.25;
      }
    }

    &::before {
      content: "";
      display: block;
      background-color: #000;
      opacity: 0.5;
      position: absolute;
      left: 0;
      top: 0;
      width: 100%;
      height: 100%;
      transition: opacity 0.1s ease-in-out;
      z-index: 1;
    }

    > img {
      display: block;
      width: 100%;
      height: auto;
      min-height: 100%;
      object-fit: cover;
      position: absolute;
    }
  }
}

.close-button {
  display: flex;
  flex-direction: column;
  justify-content: center;
  opacity: 0.65;
  height: 32px;
  width: 32px;
  transition: opacity 0.1s ease-in-out;

  > span {
    background-color: #fff;
    display: block;
    margin: -1px auto;
    width: 28px;
    height: 2px;

    &:first-of-type {
      transform: rotate(45deg);
    }
    &:last-of-type {
      transform: rotate(-45deg);
    }
  }

  &:hover {
    cursor: pointer;
    opacity: 1;
  }
}

.gallery {
  &__stage-img {
    background-color: $gray-00;
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
    height: 65vw;
    max-height: 300px;

    @media (min-width: $screen-xs) {
      height: 360px;
      max-height: none;
    }

    @media (min-width: $screen-sm) {
      height: 460px;
    }

    > img {
      display: block;
      width: 100%;
      height: auto;
      min-height: 100%;
      object-fit: cover;
      position: absolute;
    }
  }
}
</style>
